import { type AxiosPromise } from "axios";
import React from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { type CommonReactSelectWrapperProps } from "../models";
import { useReactSelectDropdown } from "../hooks";
import { components } from "react-select";
import ErrorTryAgainLabel from "../ErrorTryAgainLabel";
import { useValidateIsNotNull } from "formvalidation/hooks";

interface Additional {
  page: number
}

interface PaginatedReactSelectWrapperProps<TOption, IsMulti extends boolean, TId> extends CommonReactSelectWrapperProps<TOption, IsMulti, TId> {
  fetchOptions: (search: string, page: number) => AxiosPromise<server.dto.ApiResponseWrapper<TOption[]>>
  isMulti?: IsMulti
}

const BasePaginatedDropdown = <TOption extends unknown, IsMulti extends boolean = false, TId = number>(props: PaginatedReactSelectWrapperProps<TOption, IsMulti, TId>) => {
  const [hadApiError, setHadApiError] = React.useState(false);

  // When the API throws an error and the user chooses to try again
  // then we increment the retries, which will cause the dropdown to re-render
  const [retries, setRetries] = React.useState(0);

  let validationRef: React.RefObject<HTMLDivElement>;
  let hasErrorFromValidator: boolean = undefined;

  if (props.config?.validatorKey) {
    const validation = useValidateIsNotNull({
      key: props.config.validatorKey,
      title: props.config.validatorKey,
      value: props.value,
    });
    validationRef = validation.ref;
    hasErrorFromValidator = validation.error.hasError;
  }

  const loadOptions = async (search: string, _: TOption[], additional: Additional) => {
    const newPage = (additional?.page ?? 0) + 1;
    setHadApiError(false);
    try {
      const result = await props.fetchOptions(search, newPage);

      return {
        options: result.data?.data ?? [],
        hasMore: result.data.pagination.page < result.data.pagination.pageCount,
        additional: {
          page: result.data.pagination.page
        }
      };
    } catch (e) {
      console.error(e);
      console.log("Error loading options, returning last results");
      setHadApiError(true);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page ?? 0
        }
      };
    }
  };

  const resetAfterError = () => {
    setHadApiError(false);
    setRetries(retries => retries + 1);
  };

  const reactSelectDropdown = useReactSelectDropdown(props, hasErrorFromValidator);

  const { isMulti = false } = props;

  return <div className="flex">
    <div ref={validationRef} className="grow1">
      <AsyncPaginate
        key={`${props.excludeKeys?.join(",") || ""}|${retries}`}
        {...reactSelectDropdown.reactSelectProps}
        selectRef={reactSelectDropdown.ref}
        loadOptions={loadOptions}
        debounceTimeout={500}
        isMulti={isMulti}
        loadOptionsOnMenuOpen
        hideSelectedOptions
        components={{
          ...reactSelectDropdown.reactSelectProps.components,
          NoOptionsMessage: noOptsProps => <components.NoOptionsMessage {...noOptsProps}>
            {hadApiError && <ErrorTryAgainLabel onTryAgain={resetAfterError} />}
            {!hadApiError && RESX.GeneralLabels.NoResultsFound}
          </components.NoOptionsMessage>
        }}
      />
    </div>
  </div>;
};

export default BasePaginatedDropdown;